import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight, PiSignOutLight, PiWarningCircleLight } from 'react-icons/pi';
import GlassCard from '../components/shared/brand/GlassCard';
import { InfoRow } from '../components/shared/brand/Field';
import Button from '../components/shared/TailwindComponents/Button';
import { ClientProfileForm } from '../components/Onboarding/ClientProfileStep';
import { Account } from '../services/api/accountService';
import { useAppDispatch } from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';

interface ClientAccountPageProps {
  account: Account;
  onAccountChange: (account: Account) => void;
  onSignOut: () => void;
}

/** A client's own account: sign-in email, name and country, sign out. */
const ClientAccountPage: React.FC<ClientAccountPageProps> = ({ account, onAccountChange, onSignOut }) => {
  const { t } = useTranslation();
  const { user } = useAuth0();
  const dispatch = useAppDispatch();

  const saved = (next: Account) => {
    onAccountChange(next);
    dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 3000, message: t('Your details are saved') }));
  };

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-medium text-fg-strong">{t('Account')}</h1>
      <GlassCard title={t('Sign-in')}>
        <InfoRow label={t('Email')} value={user?.email || ''} />
        <InfoRow
          label={t('Email confirmed')}
          value={
            account.email_verified ? (
              <span className="inline-flex items-center gap-1 text-success-600">
                <PiCheckCircleLight className="h-4 w-4" />
                {t('Yes')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-warning-600">
                <PiWarningCircleLight className="h-4 w-4" />
                {t('Not yet')}
              </span>
            )
          }
        />
      </GlassCard>
      <ClientProfileForm account={account} onSaved={saved} mode="edit" />
      <div>
        <Button variant="secondary" size="small" onClick={onSignOut}>
          <PiSignOutLight className="h-4 w-4" />
          {t('Sign out')}
        </Button>
      </div>
    </div>
  );
};

export default ClientAccountPage;
